/**
 * ChangeOrderModal — Create / edit form for a numbered Change Order that
 * mirrors the ProCall CO template exactly.
 *
 * Sections:
 *   • Additional Work section title + context paragraph
 *   • Scope bullets (add/remove rows)
 *   • Line items (Description / Details / Amount) with live subtotal
 *   • Optional Deep Courtesy Discount (green highlight in the PDF)
 *   • Payment Terms bullets
 *
 * Buttons:
 *   • Save Draft   — persist as draft (editable later)
 *   • Save + Finalize   — locks + mints share token
 *   • Preview PDF  — opens admin PDF in new tab
 */
import { useMemo, useState, useEffect } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  X, Plus, Trash, Sparkle, FloppyDisk, Eye, LockKey, Warning,
} from "@phosphor-icons/react";

const money = (cents) => {
  const v = Math.abs(cents || 0) / 100;
  return v % 1 ? `$${v.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : `$${Math.round(v).toLocaleString()}`;
};

const DEFAULT_PAYMENT_TERMS = [
  "$0.00 due with the final payment of the original contract.",
  "Remaining balance held and split into equal monthly payments beginning after the property is rented.",
];

const emptyLine = () => ({ description: "", details: "", amount_cents: 0 });

export default function ChangeOrderModal({ jobId, existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const [form, setForm] = useState({
    section_title: existing?.section_title || "",
    context: existing?.context || "",
    scope_bullets: existing?.scope_bullets?.length ? existing.scope_bullets : [""],
    line_items: existing?.line_items?.length ? existing.line_items : [emptyLine()],
    discount_label: existing?.discount_label || "",
    discount_cents: existing?.discount_cents || 0,
    payment_terms: existing?.payment_terms?.length ? existing.payment_terms : DEFAULT_PAYMENT_TERMS,
    original_contract_label: existing?.original_contract_label || "Repair & Make-Ready Agreement (previously executed)",
  });
  const [busy, setBusy] = useState(false);

  const upd = (k) => (v) => setForm(s => ({ ...s, [k]: v }));
  const updLine = (i, k, v) =>
    setForm(s => ({
      ...s,
      line_items: s.line_items.map((row, idx) => idx === i ? { ...row, [k]: v } : row),
    }));
  const addLine = () => setForm(s => ({ ...s, line_items: [...s.line_items, emptyLine()] }));
  const rmLine = (i) => setForm(s => ({ ...s, line_items: s.line_items.filter((_, idx) => idx !== i) }));
  const updBullet = (i, v) =>
    setForm(s => ({ ...s, scope_bullets: s.scope_bullets.map((x, idx) => idx === i ? v : x) }));
  const addBullet = () => setForm(s => ({ ...s, scope_bullets: [...s.scope_bullets, ""] }));
  const rmBullet = (i) => setForm(s => ({ ...s, scope_bullets: s.scope_bullets.filter((_, idx) => idx !== i) }));
  const updTerm = (i, v) =>
    setForm(s => ({ ...s, payment_terms: s.payment_terms.map((x, idx) => idx === i ? v : x) }));
  const addTerm = () => setForm(s => ({ ...s, payment_terms: [...s.payment_terms, ""] }));
  const rmTerm = (i) => setForm(s => ({ ...s, payment_terms: s.payment_terms.filter((_, idx) => idx !== i) }));

  const subtotal = useMemo(
    () => (form.line_items || []).reduce((a, x) => a + (parseInt(x.amount_cents) || 0), 0),
    [form.line_items]
  );
  const total = Math.max(0, subtotal - (parseInt(form.discount_cents) || 0));

  const buildPayload = () => ({
    ...form,
    scope_bullets: (form.scope_bullets || []).filter(x => (x || "").trim()),
    line_items: (form.line_items || []).filter(x => (x.description || "").trim() || (x.amount_cents || 0)),
    payment_terms: (form.payment_terms || []).filter(x => (x || "").trim()),
    discount_cents: parseInt(form.discount_cents) || 0,
  });

  const save = async ({ finalize = false } = {}) => {
    if (!form.section_title.trim()) { toast.error("Section title is required"); return; }
    setBusy(true);
    try {
      let co;
      if (isEdit) {
        co = (await api.patch(`/change-orders/${existing.id}`, buildPayload())).data;
      } else {
        co = (await api.post(`/jobs/${jobId}/change-orders`, buildPayload())).data;
      }
      if (finalize) {
        co = (await api.post(`/change-orders/${co.id}/finalize`)).data;
        toast.success(`Change Order #${co.co_number} finalized & link ready`);
      } else {
        toast.success("Draft saved");
      }
      onSaved?.(co);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed"));
    } finally { setBusy(false); }
  };

  const preview = async () => {
    if (!isEdit) { toast.info("Save the draft first, then preview."); return; }
    const token = localStorage.getItem("access_token");
    const u = `${API}/change-orders/${existing.id}/pdf`;
    try {
      const res = await fetch(u, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("preview failed");
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("Preview failed"); }
  };

  const setAmountFromInput = (i, str) => {
    // Accept "$1,200", "1200", "1,200.50" and store as cents
    const clean = String(str || "").replace(/[^\d.]/g, "");
    if (!clean) return updLine(i, "amount_cents", 0);
    const asFloat = parseFloat(clean);
    if (isNaN(asFloat)) return;
    updLine(i, "amount_cents", Math.round(asFloat * 100));
  };
  const setDiscountFromInput = (str) => {
    const clean = String(str || "").replace(/[^\d.]/g, "");
    const asFloat = parseFloat(clean) || 0;
    upd("discount_cents")(Math.round(asFloat * 100));
  };

  return (
    <div className="fixed inset-0 bg-slate-900/70 z-[85] flex items-start justify-center p-2 sm:p-4"
      data-testid="co-modal">
      <div className="bg-white w-full max-w-3xl max-h-[95vh] flex flex-col border-4 border-slate-900 shadow-[6px_6px_0_#0f172a] overflow-hidden">
        <div className="p-3 bg-gradient-to-r from-indigo-700 to-violet-800 text-white flex items-start justify-between sticky top-0 z-10">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest opacity-80 flex items-center gap-1">
              <Sparkle weight="fill" size={11}/> Change Order Generator
            </div>
            <div className="font-black uppercase tracking-widest text-lg">
              {isEdit ? `Change Order #${existing.co_number}` : "New Change Order"}
            </div>
          </div>
          <button onClick={onClose} data-testid="co-close" className="p-1 hover:bg-black/20"><X size={20}/></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Additional Work title */}
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Additional Work — Section Title *</label>
            <input value={form.section_title} onChange={e => upd("section_title")(e.target.value)}
              placeholder="e.g. 3rd Floor Large Room (Purple Room)"
              className="mt-1 w-full h-10 px-3 border-2 border-slate-300 focus:border-indigo-600 text-sm outline-none"
              data-testid="co-section-title"/>
          </div>

          {/* Context paragraph */}
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">
              Why this change is needed (paragraph)
            </label>
            <textarea value={form.context} onChange={e => upd("context")(e.target.value)}
              rows={2}
              placeholder="e.g. Previously hidden conditions discovered after removal of ceiling paper..."
              className="mt-1 w-full p-2 border-2 border-slate-300 focus:border-indigo-600 text-sm outline-none"
              data-testid="co-context"/>
          </div>

          {/* Scope bullets */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Scope Bullets</label>
              <button onClick={addBullet}
                className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-0.5"
                data-testid="co-add-bullet">
                <Plus weight="bold" size={11}/> Add
              </button>
            </div>
            <div className="space-y-1">
              {form.scope_bullets.map((b, i) => (
                <div key={i} className="flex gap-1">
                  <input value={b} onChange={e => updBullet(i, e.target.value)}
                    className="flex-1 h-9 px-2 border border-slate-200 text-sm bg-white"
                    placeholder="e.g. Remove 5 damaged roof joists..."
                    data-testid={`co-bullet-${i}`}/>
                  <button onClick={() => rmBullet(i)} className="w-8 text-slate-400 hover:text-red-500 flex items-center justify-center">
                    <Trash weight="bold" size={12}/>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                Fair Market Value Items · Subtotal: <b className="text-slate-900">{money(subtotal)}</b>
              </label>
              <button onClick={addLine}
                className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-0.5"
                data-testid="co-add-line">
                <Plus weight="bold" size={11}/> Add Line
              </button>
            </div>
            <div className="space-y-1">
              {form.line_items.map((li, i) => (
                <div key={i} className="grid grid-cols-12 gap-1">
                  <input value={li.description} onChange={e => updLine(i, "description", e.target.value)}
                    placeholder="Description"
                    className="col-span-3 h-9 px-2 border border-slate-200 text-sm bg-white"
                    data-testid={`co-line-desc-${i}`}/>
                  <input value={li.details} onChange={e => updLine(i, "details", e.target.value)}
                    placeholder="Details"
                    className="col-span-6 h-9 px-2 border border-slate-200 text-sm bg-white"
                    data-testid={`co-line-details-${i}`}/>
                  <input value={li.amount_cents ? (li.amount_cents / 100).toString() : ""}
                    onChange={e => setAmountFromInput(i, e.target.value)}
                    placeholder="0"
                    className="col-span-2 h-9 px-2 border border-slate-200 text-sm text-right bg-white"
                    data-testid={`co-line-amount-${i}`}/>
                  <button onClick={() => rmLine(i)} className="col-span-1 text-slate-400 hover:text-red-500 flex items-center justify-center">
                    <Trash weight="bold" size={12}/>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Discount */}
          <div className="rounded-md bg-emerald-50 border-2 border-emerald-200 p-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-emerald-900">
              Courtesy Discount (optional — appears as green row on the PDF)
            </label>
            <div className="grid grid-cols-12 gap-1 mt-1">
              <input value={form.discount_label} onChange={e => upd("discount_label")(e.target.value)}
                placeholder="e.g. Deep Courtesy Discount (≈ 42%)"
                className="col-span-9 h-9 px-2 border border-emerald-300 text-sm bg-white"
                data-testid="co-discount-label"/>
              <input value={form.discount_cents ? (form.discount_cents / 100).toString() : ""}
                onChange={e => setDiscountFromInput(e.target.value)}
                placeholder="0"
                className="col-span-3 h-9 px-2 border border-emerald-300 text-sm text-right bg-white"
                data-testid="co-discount-amount"/>
            </div>
          </div>

          {/* Total */}
          <div className="rounded-md bg-blue-100 border-2 border-blue-300 p-3 flex items-center justify-between">
            <div className="text-xs font-black uppercase tracking-widest text-blue-900">Change Order Amount</div>
            <div className="text-2xl font-black text-blue-900" data-testid="co-total">{money(total)}</div>
          </div>

          {/* Payment terms */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Payment Terms</label>
              <button onClick={addTerm}
                className="text-[10px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-0.5">
                <Plus weight="bold" size={11}/> Add Term
              </button>
            </div>
            <div className="space-y-1">
              {form.payment_terms.map((t, i) => (
                <div key={i} className="flex gap-1">
                  <input value={t} onChange={e => updTerm(i, e.target.value)}
                    className="flex-1 h-9 px-2 border border-slate-200 text-sm bg-white"
                    data-testid={`co-term-${i}`}/>
                  <button onClick={() => rmTerm(i)} className="w-8 text-slate-400 hover:text-red-500 flex items-center justify-center">
                    <Trash weight="bold" size={12}/>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Original contract label */}
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Original Contract Label</label>
            <input value={form.original_contract_label} onChange={e => upd("original_contract_label")(e.target.value)}
              className="mt-1 w-full h-9 px-2 border-2 border-slate-300 text-sm bg-white"
              data-testid="co-original-contract"/>
          </div>
        </div>

        {/* Sticky footer actions */}
        <div className="p-3 border-t-2 border-slate-200 bg-slate-50 flex flex-wrap items-center gap-2">
          {isEdit && (
            <button onClick={preview} disabled={busy}
              className="h-10 px-3 bg-white border-2 border-slate-300 text-slate-700 text-[11px] font-black uppercase tracking-widest disabled:opacity-50 inline-flex items-center gap-1"
              data-testid="co-preview">
              <Eye weight="bold" size={12}/> Preview PDF
            </button>
          )}
          <div className="flex-1"/>
          <button onClick={() => save({ finalize: false })} disabled={busy}
            className="h-10 px-3 bg-white border-2 border-indigo-400 text-indigo-700 text-[11px] font-black uppercase tracking-widest disabled:opacity-50 inline-flex items-center gap-1"
            data-testid="co-save-draft">
            <FloppyDisk weight="fill" size={12}/> Save Draft
          </button>
          <button onClick={() => save({ finalize: true })} disabled={busy}
            className="h-10 px-4 bg-gradient-to-r from-indigo-600 to-violet-700 text-white text-[11px] font-black uppercase tracking-widest disabled:opacity-50 inline-flex items-center gap-1 shadow-[3px_3px_0_#0f172a]"
            data-testid="co-finalize">
            <LockKey weight="fill" size={12}/> Save & Finalize
          </button>
        </div>
      </div>
    </div>
  );
}
